const HERO_IMAGE =
  'https://images.unsplash.com/photo-1552674605-db6ffd4facb5?auto=format&fit=crop&w=1600&q=80';

/** Featured guide: triathlon race day checklist from week-before through finish */
export const TRIATHLON_RACE_DAY_CHECKLIST_POST = {
  id: 'featured-triathlon-race-day-checklist',
  slug: 'triathlon-race-day-checklist',
  title: 'Triathlon Race Day Checklist: Week-Before to Finish Line',
  excerpt:
    'A complete printable-style checklist for sprint and Olympic triathlons—gear packing, transition setup, swim/bike/run execution, and post-race recovery. Not medical advice.',
  publishedAt: '2026-07-23T11:55:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Endurance Desk',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'More race prep guides',
  relatedPosts: [
    {
      slug: 'triathlon-running-shoes-wetsuits-nutrition-guide',
      title: 'Running Shoes, Wetsuits & Race Nutrition Guide',
      blurb: 'pick and test gear before you pack this list.',
    },
    {
      slug: 'triathlon-beginner-training-plan-one-month-before-race',
      title: 'Beginner Triathlon Plan (300 m · 7 mi · 1.5 mi)',
      blurb: 'your training plan leading into race week.',
    },
    {
      slug: 'triathlon-sprint-training-plan-one-month-before-race',
      title: 'Sprint Triathlon Plan (750 m · 13 mi · 5K)',
      blurb: 'sprint-distance taper and race week.',
    },
  ],
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Checklist overview',
      items: [
        { value: '5 phases', label: 'Week · eve · AM · race · after' },
        { value: '2 bags', label: 'Transition + morning' },
        { value: 'Sprint–Olympic', label: 'Distance notes' },
        { value: 'Printable', label: 'Work through in order' },
      ],
    },
    {
      type: 'panel',
      title: 'How to use this checklist',
      paragraphs: [
        'Work through each section in order starting one week out. Check items off in your phone notes or print this page. Sprint athletes can skip items marked “Olympic only.”',
        'Lay out gear the night before. Race morning is for execution, not decision-making. Nothing on this list should be new—if you have not tested it in training, leave it home.',
      ],
      chips: ['Sprint & Olympic', 'T1 & T2', 'Ohio races', 'First-timer friendly'],
    },
    {
      type: 'panel',
      title: 'One week before race',
      orderedList: [
        'Confirm race registration, wave start time, and transition bag rules.',
        'Review course maps: swim entry, bike mount line, run exit, aid station locations.',
        'Check weather forecast—adjust wetsuit, hydration, and clothing plan.',
        'Reduce training volume (taper); no hard sessions within 3 days of race.',
        'Prep nutrition: buy only gels/drinks you have used in training.',
        'Charge GPS watch, bike computer, and any lights required by race rules.',
        'Clean bike: check tires, brakes, chain, and bolt tightness.',
        'Plan drive/parking—arrive 75–90 min before your wave (sprint) or 90+ min (Olympic).',
      ],
    },
    {
      type: 'panel',
      title: 'Night before — pack these bags',
      list: [
        'Transition bag (T1/T2): helmet, sunglasses, bike shoes, run shoes with elastic laces, race belt with bib pinned, socks (if using), small towel to mark spot.',
        'Swim bag: goggles (plus spare), swim cap (race often provides one—bring yours too), wetsuit, body glide for neck and ankles, flip-flops to walk to swim start.',
        'Morning bag: post-race clothes, sandals, warm layer, recovery snack, phone charger.',
        'Bike: bottles filled, gels taped to frame or in bento box (Olympic), CO₂ cartridges or pump, spare tube + levers (sprint optional but smart).',
        'Lay out tri suit or race outfit, timing chip strap, and anything you wear from wake-up to start.',
      ],
    },
    {
      type: 'table',
      title: 'Race morning timeline (example for 8:00 a.m. wave)',
      intro: 'Shift times if your wave starts earlier or later. Eat only familiar foods.',
      headers: ['Time', 'Action', 'Details'],
      rows: [
        ['5:00 a.m.', 'Wake + light breakfast', 'Oatmeal, toast, banana—300–500 cal; 16–20 oz water'],
        ['5:45 a.m.', 'Leave for venue', 'Allow traffic and parking stress buffer'],
        ['6:15 a.m.', 'Check in + rack bike', 'Helmet on bars, bib on bike or in transition bag per rules'],
        ['6:30 a.m.', 'Set up transition', 'Towel, shoes open, sunglasses in helmet, run gear behind bike'],
        ['6:45 a.m.', 'Body mark + timing chip', 'Confirm both ankles if required'],
        ['7:00 a.m.', 'Wetsuit on + bathroom', 'Apply body glide at neck, wrists, ankles'],
        ['7:20 a.m.', 'Short warm-up', 'Easy jog 5 min + 5–8 min swim if allowed'],
        ['7:45 a.m.', 'Line up at swim start', 'Stay calm; breathe slowly'],
        ['8:00 a.m.', 'Race start', 'Execute your plan—not anyone else’s pace'],
      ],
    },
    {
      type: 'panel',
      title: 'Swim start checklist',
      list: [
        'Goggles on tight; spare pair in transition bag just in case.',
        'Know your start format: deep-water treading vs. beach run-in.',
        'Start conservatively—first 100 m easy; sprinting the swim wastes matches.',
        'Sight every 6–10 strokes in open water; follow feet only if you trust the pace.',
        'At exit: unzip wetsuit to waist while running to transition; peel at your bike.',
      ],
    },
    {
      type: 'panel',
      title: 'T1 (swim → bike) checklist',
      orderedList: [
        'Run to your rack—know your row/landmark (bright towel helps).',
        'Peel wetsuit; step on it with one foot if needed.',
        'Dry feet quickly with towel; body glide on heels if sockless.',
        'Tri suit zipped; helmet ON and buckled before you touch the bike (USAT rule).',
        'Sunglasses in helmet, then shoes; grab bike and run to mount line.',
        'Mount only past the line; clip in when stable—easy first 2 km.',
      ],
    },
    {
      type: 'panel',
      title: 'Bike leg checklist',
      list: [
        'Hold back first 10 minutes—adrenaline lies to you.',
        'Drink early: sip every 10–15 min (Olympic: aim for 1 bottle per hour).',
        'Eat on schedule, not when hungry—Olympic: 30–60 g carbs/hour if trained.',
        'Stay legal: know draft zone rules for your race (often 3 bike lengths).',
        'Pedal easy final 1–2 km to loosen legs before T2.',
        'Dismount before the line; run bike to rack.',
      ],
    },
    {
      type: 'panel',
      title: 'T2 (bike → run) checklist',
      orderedList: [
        'Rack bike in same spot; helmet off only after bike is racked.',
        'Switch to run shoes (elastic laces = seconds saved).',
        'Race belt on if not worn under wetsuit; hat/visor optional.',
        'Start run SLOW—first 400 m should feel too easy (heavy legs are normal).',
        'Find rhythm by mile 1; then settle into goal pace.',
      ],
    },
    {
      type: 'panel',
      title: 'Run leg checklist',
      list: [
        'Walk aid stations if needed—drink, pour water on head in heat.',
        'Olympic only: gel or chews on schedule you practiced on the bike.',
        'Shorten stride on hills; do not attack climbs like a standalone 5K.',
        'Final 400 m: pick up pace if you have it; finish with a smile for photos.',
        'Keep moving through the finish chute; collect medal, water, and wrap.',
      ],
    },
    {
      type: 'panel',
      title: 'Post-race checklist',
      orderedList: [
        'Walk 5–10 min; do not sit immediately.',
        'Water + carbs within 30 min (chocolate milk, banana, recovery bar).',
        'Change out of tri suit when possible; dry clothes prevent chills.',
        'Light stretch calves, quads, hip flexors.',
        'Log the race: what worked, what to fix (nutrition, pacing, transitions).',
        'Clean bike and wash wetsuit with fresh water when home.',
      ],
    },
    {
      type: 'table',
      title: 'Master gear list — tick before you leave home',
      intro: 'Sprint athletes can skip items marked optional. Olympic athletes should carry fuel on the bike.',
      headers: ['Item', 'Swim', 'Bike', 'Run', 'Transition'],
      rows: [
        ['Tri suit or race outfit', '✓', '✓', '✓', '—'],
        ['Wetsuit (if legal)', '✓', '—', '—', 'T1 peel'],
        ['Goggles + spare', '✓', '—', '—', '—'],
        ['Swim cap', '✓', '—', '—', '—'],
        ['Helmet (buckled on bike)', '—', '✓', '—', 'T1'],
        ['Bike shoes + pedals', '—', '✓', '—', 'T1'],
        ['Water bottles filled', '—', '✓', '—', 'Pre-rack'],
        ['Gels / chews (Olympic)', '—', '✓', 'Optional', '—'],
        ['Run shoes + elastic laces', '—', '—', '✓', 'T2'],
        ['Race belt + bib', '—', '—', '✓', 'T1 or T2'],
        ['Sunglasses', '—', '✓', 'Optional', 'Helmet'],
        ['Body glide / chamois cream', '✓', '✓', '—', '—'],
        ['Timing chip + strap', '✓', '✓', '✓', 'Ankle'],
        ['CO₂ / pump + spare tube (optional)', '—', '✓', '—', 'On bike'],
        ['Post-race clothes + sandals', '—', '—', '—', 'Morning bag'],
      ],
    },
    {
      type: 'panel',
      title: 'Mental race-day reminders',
      list: [
        'You trained for this—trust the taper even if you feel restless.',
        'Something will go imperfect (goggle fog, slow zipper)—adapt and keep moving.',
        'Race your plan: negative split run beats hero swim start.',
        'Thank volunteers at aid stations—they make the sport possible.',
        'First triathlon finish is always a PR—enjoy it.',
      ],
      callout:
        'Screenshot or print the master gear table the night before. Race morning brain fog is real—even experienced triathletes forget sunglasses.',
    },
  ],
  referenceLinks: [
    {
      label: 'USA Triathlon — race-day rules & transitions',
      url: 'https://www.usatriathlon.org/resources/multisport-101',
    },
    {
      label: 'Ironman — race-morning tips',
      url: 'https://www.ironman.com/news',
    },
    {
      label: 'TrainingPeaks — triathlon race-day checklist',
      url: 'https://www.trainingpeaks.com/blog/',
    },
  ],
};
